import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { ArrowLeft } from "lucide-react";
import ContentHeader from "../components/ContentHeader";
import ArticleAudio from "../components/ArticleAudio";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = "https://mortgagesurvivalplaybook.com";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch { return ""; }
};

const setMeta = (name, content, attr = "name") => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const Article = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let ldScript;
    setState("loading");
    axios.get(`${API}/articles/${slug}`)
      .then(({ data }) => {
        setArticle(data);
        setState("ready");
        // SEO meta (Googlebot renders JS and reads these)
        document.title = `${data.title} | Mortgage Survival Playbook 2026`;
        setMeta("description", data.meta_description);
        setMeta("og:title", data.title, "property");
        setMeta("og:description", data.meta_description, "property");
        setMeta("og:type", "article", "property");
        setMeta("og:url", `${SITE}/insights/${data.slug}`, "property");
        setMeta("og:image", `${SITE}/assets/og-image.jpg`, "property");
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", data.title);
        setMeta("twitter:description", data.meta_description);
        setMeta("twitter:image", `${SITE}/assets/og-image.jpg`);
        let canon = document.querySelector('link[rel="canonical"]');
        if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
        canon.setAttribute("href", `${SITE}/insights/${data.slug}`);
        // Article + FAQ JSON-LD
        const graph = [{
          "@type": "Article",
          "headline": data.title,
          "description": data.meta_description,
          "author": { "@type": "Person", "name": data.author || "Lloyd Igbokwe" },
          "publisher": { "@type": "Organization", "name": "Mortgage Survival Playbook", "logo": { "@type": "ImageObject", "url": `${SITE}/assets/logo-institutional.png` } },
          "datePublished": data.published_at || data.created_at,
          "dateModified": data.published_at || data.created_at,
          "mainEntityOfPage": `${SITE}/insights/${data.slug}`,
          "keywords": (data.tags || []).join(", "),
        }];
        if (data.faq?.length) {
          graph.push({
            "@type": "FAQPage",
            "mainEntity": data.faq.map((f) => ({
              "@type": "Question", "name": f.q,
              "acceptedAnswer": { "@type": "Answer", "text": f.a },
            })),
          });
        }
        ldScript = document.createElement("script");
        ldScript.type = "application/ld+json";
        ldScript.setAttribute("data-article-ld", "true");
        ldScript.text = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
        document.head.appendChild(ldScript);
      })
      .catch(() => setState("notfound"));

    return () => {
      const s = document.querySelector('script[data-article-ld="true"]');
      if (s) s.remove();
    };
  }, [slug]);

  return (
    <div style={{ minHeight: "100vh" }} data-testid="article-page">
      <ContentHeader />
      <div className="hero-bg" />
      <article className="article-wrap">
        <button className="article-back" onClick={() => navigate("/insights")} data-testid="article-back">
          <ArrowLeft size={15} /> All Insights
        </button>

        {state === "loading" && <p style={{ color: "var(--g1)" }} data-testid="article-loading">Loading…</p>}

        {state === "notfound" && (
          <div data-testid="article-notfound">
            <h1 className="article-title">Article not found</h1>
            <p style={{ color: "var(--g1)" }}>This article may have been unpublished. Browse our latest insights instead.</p>
          </div>
        )}

        {state === "ready" && article && (
          <>
            {article.tags?.length > 0 && (
              <div className="article-tags">
                {article.tags.map((t) => <span className="insight-tag" key={t}>{t}</span>)}
              </div>
            )}
            <h1 className="article-title" data-testid="article-title">{article.title}</h1>
            <div className="article-byline">
              By <span className="gold">{article.author || "Lloyd Igbokwe"}</span> · {formatDate(article.published_at || article.created_at)}
            </div>
            <ArticleAudio title={article.title} html={article.body_html} />
            <div
              className="article-body"
              data-testid="article-body"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.body_html) }}
            />

            {article.faq?.length > 0 && (
              <div className="article-faq" data-testid="article-faq">
                <h2>Frequently Asked Questions</h2>
                {article.faq.map((f, i) => (
                  <div className="article-faq-item" key={f.q || i}>
                    <h4>{f.q}</h4>
                    <p>{f.a}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="article-cta" data-testid="article-cta">
              <h3>The Mortgage Survival Playbook 2026</h3>
              <p>A ready-to-deploy institutional framework to identify borrower risk earlier and prevent avoidable foreclosures.</p>
              <button onClick={() => navigate("/#purchase")} data-testid="article-cta-btn">
                Explore the Institutional License →
              </button>
            </div>
          </>
        )}
      </article>
      <Footer />
    </div>
  );
};

export default Article;
